import * as React from "react";
import { makeStyles, mergeClasses, Text, Button, Spinner } from "@fluentui/react-components";
import CardRouter from "./cards/CardRouter";

const useStyles = makeStyles({
  // ── Message row ──
  row: {
    display: "flex",
    flexDirection: "column",
    padding: "4px 16px",
    gap: "4px",
  },
  rowUser: {
    alignItems: "flex-end",
  },
  rowAssistant: {
    alignItems: "flex-start",
  },

  // ── Bubble ──
  bubble: {
    maxWidth: "88%",
    padding: "10px 14px",
    borderRadius: "14px",
    fontSize: "13px",
    lineHeight: "1.5",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  bubbleUser: {
    backgroundColor: "#FFB500",
    color: "#0B111A",
    borderBottomRightRadius: "4px",
    fontWeight: "500",
  },
  bubbleAssistant: {
    backgroundColor: "var(--bg-tertiary, #F0F1F3)",
    color: "var(--text-primary, #0B111A)",
    borderBottomLeftRadius: "4px",
    border: "1px solid var(--border-primary, #E0E2E6)",
  },
  bubbleError: {
    backgroundColor: "var(--color-error-bg, #FFF0F0)",
    borderColor: "var(--color-error, #FF5757)",
    color: "var(--text-primary, #0B111A)",
  },

  // ── Loading indicator ──
  loadingRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 14px",
    backgroundColor: "var(--bg-tertiary, #F0F1F3)",
    borderRadius: "14px",
    borderBottomLeftRadius: "4px",
    border: "1px solid var(--border-primary, #E0E2E6)",
    maxWidth: "88%",
  },
  loadingText: {
    fontSize: "12px",
    color: "var(--color-info, #13A3E0)",
    fontStyle: "italic",
  },

  // ── Options (quick-select buttons from follow-up questions) ──
  optionsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    maxWidth: "88%",
    marginTop: "2px",
  },
  optionBtn: {
    fontSize: "12px",
    minWidth: "auto",
    padding: "4px 12px",
    borderRadius: "9999px",
    border: "1px solid var(--border-secondary, #C8CBD0)",
    backgroundColor: "var(--bg-elevated, #FFFFFF)",
    color: "var(--text-primary, #0B111A)",
    cursor: "pointer",
    fontWeight: "500",
    transition: "all 0.15s ease",
    ":hover": {
      backgroundColor: "var(--bg-active, #FFF5D6)",
      borderColor: "#FFB500",
      color: "#0B111A",
    },
  },

  // ── Markdown rendering ──
  markdownBody: {
    "& p": {
      margin: "0 0 6px 0",
    },
    "& p:last-child": {
      marginBottom: 0,
    },
    "& strong": {
      fontWeight: "600",
      color: "var(--text-primary, #0B111A)",
    },
    "& ul, & ol": {
      paddingLeft: "18px",
      margin: "4px 0",
    },
    "& li": {
      marginBottom: "4px",
      lineHeight: "1.45",
    },
  },

  // ── Cards container (inline in chat) ──
  cardsContainer: {
    maxWidth: "100%",
    marginTop: "4px",
  },

  // ── Timestamp ──
  timestamp: {
    fontSize: "10px",
    color: "var(--text-tertiary, #6E747D)",
    marginTop: "2px",
    paddingLeft: "2px",
    paddingRight: "2px",
  },
});

/**
 * Render simple markdown-like text to React elements.
 * Handles: **bold**, - bullets, paragraphs.
 */
function renderSimpleMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");

  return lines.map((line, idx) => {
    const trimmed = line.trim();
    if (!trimmed) return <br key={idx} />;

    // Bullet
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      const bulletText = trimmed.slice(2);
      return (
        <p key={idx} style={{ margin: "2px 0", paddingLeft: "14px", position: "relative" }}>
          <span style={{ position: "absolute", left: "2px", color: "#FFB500", fontWeight: 700 }}>{"•"}</span>
          {renderBold(bulletText)}
        </p>
      );
    }

    return (
      <p key={idx} style={{ margin: "2px 0" }}>
        {renderBold(trimmed)}
      </p>
    );
  });
}

function renderBold(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function formatTime(isoString) {
  try {
    const d = new Date(isoString);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

/**
 * ChatMessage — renders a single message bubble.
 *
 * Props:
 *   - message: { id, role, text, timestamp, cards?, options?, isLoading?, loadingText?, isError? }
 *   - onOptionSelect: (label: string) => void
 *   - onExecuteAction: Function (for action items in cards)
 *   - actionStates: Object
 *   - emailContext: Object
 */
const ChatMessage = ({ message, onOptionSelect, onExecuteAction, actionStates, emailContext }) => {
  const styles = useStyles();
  const isUser = message.role === "user";

  // ── Loading indicator ──
  if (message.isLoading) {
    return (
      <div className={mergeClasses(styles.row, styles.rowAssistant)}>
        <div className={styles.loadingRow}>
          <Spinner size="tiny" />
          <Text className={styles.loadingText}>{message.loadingText || "Processing..."}</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={mergeClasses(styles.row, isUser ? styles.rowUser : styles.rowAssistant)}>
      {/* Text bubble */}
      {message.text && (
        <div
          className={mergeClasses(
            styles.bubble,
            isUser ? styles.bubbleUser : styles.bubbleAssistant,
            message.isError && styles.bubbleError
          )}
        >
          <div className={styles.markdownBody}>
            {isUser ? message.text : renderSimpleMarkdown(message.text)}
          </div>
        </div>
      )}

      {/* Options (follow-up question choices) */}
      {message.options && message.options.length > 0 && (
        <div className={styles.optionsContainer}>
          {message.options.map((opt) => (
            <Button
              key={opt}
              className={styles.optionBtn}
              size="small"
              onClick={() => onOptionSelect && onOptionSelect(opt)}
            >
              {opt}
            </Button>
          ))}
        </div>
      )}

      {/* Inline cards (n8n response) */}
      {message.cards && message.cards.length > 0 && (
        <div className={styles.cardsContainer}>
          <CardRouter
            cards={message.cards}
            onExecuteAction={onExecuteAction}
            actionStates={actionStates || {}}
            emailContext={emailContext || {}}
          />
        </div>
      )}

      {/* Timestamp */}
      {message.timestamp && (
        <Text className={styles.timestamp}>{formatTime(message.timestamp)}</Text>
      )}
    </div>
  );
};

export default ChatMessage;
